import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';

import { DesignState } from '@editor/types';
import useResizeObserver from '@react-hook/resize-observer';
import { useIntersectionObserver } from '@repo/react-hooks/useIntersectionObserver';
import { SkeletonLayout } from '@repo/ui/components/Loader';

import { getArtboardOptions } from '../../utils/multiArtboards';

import {
  Wrapper,
  MasonryColumn,
  ElementWrapper,
  SkeletonWrapper,
} from './styles';
import { Column, ColumnItem, Item, MasonryProps } from './types';
import { calculateColumns, getItemHeight } from './utils';

// Number of skeleton items to show when the masonry is loading
const SKELETON_ITEMS_COUNT = 5;

const Masonry = <TItem extends Item>({
  minColumnWidth,
  gap,
  fetch,
  render,
  computeKey,
  transitioning,
  scrollToTop = 0,
  specialComponent,
  specialComponentHeight,
  wrapperHeight,
  equalDimensions,
  dataTestId,
  isLoading,
  ...props
}: MasonryProps<TItem>): React.ReactElement => {
  const wrapper = useRef<HTMLDivElement>(null);
  const [wrapperSize, setWrapperSize] = useState<number | null>(null);
  const [columns, setColumns] = useState<Column<TItem>[]>([]);

  const [specialElemDimensions, setSpecialElemDimensions] = useState({
    width: 0,
    height: 0,
  });

  const buildColumns = useCallback((): Column<TItem>[] => {
    if (!wrapperSize) {
      return [];
    }
    const { columnWidth, columnNumber } = calculateColumns(
      wrapperSize,
      minColumnWidth,
      gap
    );
    if (!columnNumber) {
      return [];
    }

    let newSpecialElemWidth =
      columnWidth * columnNumber + (columnNumber - 1) * gap;

    // this shouldn't be the case but it is better to be safe.
    newSpecialElemWidth = Math.min(newSpecialElemWidth, wrapperSize);

    setSpecialElemDimensions({
      width: newSpecialElemWidth,
      height: specialComponentHeight ?? 0,
    });

    const columns = Array(columnNumber)
      .fill(null)
      .map(() => ({
        elements: [] as ColumnItem<TItem>[],
        height: 0,
        isLoader: false,
      }));

    if (specialComponentHeight) {
      if (columns?.length >= 1) {
        columns[0].height += specialComponentHeight;
      }
      if (columns?.length >= 2) {
        columns[1].height += specialComponentHeight;
      }
    }

    const items = [...props.items].reverse();

    const findShortestColumn = (): number => {
      let shortest = 0;
      columns.forEach((col, idx) => {
        if (col.height < columns[shortest].height) {
          shortest = idx;
        }
      });
      return shortest;
    };

    while (items.length) {
      const item = items[items.length - 1];
      const shortestColumnIndex = findShortestColumn();
      const itemHeight = getItemHeight(item, columnWidth, equalDimensions);
      columns[shortestColumnIndex].elements.push({
        ...item,
        elementHeight: itemHeight,
        elementWidth: columnWidth,
        artboardOptions: item?.state
          ? getArtboardOptions(item.state as DesignState)
          : undefined,
        isLoader: false,
      });
      columns[shortestColumnIndex].height += itemHeight;
      items.pop();
    }

    // Find what element to use as loader
    const shortestColumn = findShortestColumn();
    const shortestColumnElements = columns[shortestColumn].elements;

    if (shortestColumnElements.length) {
      shortestColumnElements[shortestColumnElements.length - 1].isLoader = true;
    } else {
      columns[shortestColumn].isLoader = true;
    }

    return columns;
  }, [
    wrapperSize,
    minColumnWidth,
    props.items,
    gap,
    specialComponentHeight,
    equalDimensions,
  ]);

  const getKey = useCallback(
    (item: ColumnItem<TItem>, index: number) => {
      if (computeKey) {
        return computeKey(item);
      }
      return item.id || index;
    },
    [computeKey]
  );

  const { ref: loaderRef, inView: loaderVisible } = useIntersectionObserver({
    threshold: 0.1,
    rootMargin: '0px 0px 100% 0px',
    root: null,
    skip: !fetch,
  });

  useEffect(() => {
    if (!fetch) {
      return;
    }
    if (loaderVisible || (!props.items.length && wrapperSize)) {
      fetch();
    }
  }, [loaderVisible, fetch, props.items.length, wrapperSize]);

  useLayoutEffect(() => {
    if (!wrapper.current) {
      return;
    }
    setWrapperSize(wrapper.current.getBoundingClientRect().width);
  }, [wrapper]);

  useResizeObserver(wrapper, (entry) => {
    // Calculating window innerWidth and body offseWitdh we get the scrollbar width
    // If there is no scrollbar scrollbarWidth will be 0
    const scrollbarWidth = window?.innerWidth - document?.body.offsetWidth;

    // Throttle taking into account the scrollbar width to avoid weird scrollbar flicker.
    // See https://app.clickup.com/t/2443ybv
    if (
      Math.abs(entry.contentRect.width - (wrapperSize ?? 0)) > scrollbarWidth
    ) {
      setWrapperSize(entry.contentRect.width);
    }
  });

  useLayoutEffect(() => {
    if (!wrapperSize) {
      return;
    }
    setColumns(buildColumns());
  }, [props.items.length, wrapperSize, buildColumns]);

  useEffect(() => {
    wrapper.current?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }, [scrollToTop]);

  const renderCol = (column: Column<TItem>, index: number): JSX.Element => {
    return (
      <MasonryColumn
        key={`column-${index}`}
        ref={(node: HTMLDivElement): void => {
          if (column.isLoader && node) {
            loaderRef(node);
          }
        }}
        gap={gap}
        minColumnWidth={minColumnWidth}
      >
        {/* If there are no elements and the loading in progress, show skeletons */}
        {!column.elements.length && isLoading
          ? [...Array(SKELETON_ITEMS_COUNT)].map((_, index) => {
              return (
                <ElementWrapper key={`skeleton_${index}`}>
                  <SkeletonLayout
                    fallback={<SkeletonWrapper isLoading={true} />}
                    isLoading={true}
                  />
                </ElementWrapper>
              );
            })
          : column.elements.map((element, index) => {
              const key = getKey(element, index);
              return element.isLoader ? (
                <ElementWrapper
                  key={key}
                  ref={loaderRef}
                  data-testid={`element-${key}-wrapper`}
                >
                  {render(element)}
                </ElementWrapper>
              ) : (
                <ElementWrapper
                  key={key}
                  data-testid={`element-${key}-wrapper`}
                >
                  {render(element)}
                </ElementWrapper>
              );
            })}
      </MasonryColumn>
    );
  };

  return (
    <Wrapper
      ref={wrapper}
      data-testid={dataTestId}
      gap={gap}
      height={wrapperHeight}
    >
      {!transitioning && (
        <>
          {!!specialComponent && (
            <MasonryColumn>
              <div
                style={{
                  width: `${specialElemDimensions.width}px`,
                  marginBottom: `${gap}px`,
                }}
              >
                {specialComponent}
              </div>
              <Wrapper
                style={{
                  width: `${specialElemDimensions.width}px`,
                }}
              >
                {columns.map((column, index) => {
                  if (index >= 2) {
                    return null;
                  }
                  return renderCol(column, index);
                })}
              </Wrapper>
            </MasonryColumn>
          )}
          {columns.map((column, index) => {
            if (specialComponent && index < 3) {
              return null;
            }
            return renderCol(column, index);
          })}
        </>
      )}
    </Wrapper>
  );
};

export default Masonry;
